import { useState, useEffect, useCallback, useRef } from 'react';
import './SkinIntelligence.css';

interface SkinIntelligenceProps {
  apiBaseUrl: string;
}

const CONCERNS: { label: string; dotX: number; dotY: number; pillSide: 'left' | 'right' | 'top'; pillX: number; pillY: number }[] = [
  { label: 'dryness',     dotX: 30, dotY: 42, pillSide: 'left',  pillX: -110, pillY: 28 },
  { label: 'uneven tone', dotX: 70, dotY: 56, pillSide: 'right', pillX: 110,  pillY: 46 },
  { label: 'fine lines',  dotX: 52, dotY: 22, pillSide: 'top',   pillX: 0,    pillY: -54 },
];

const SCALE_COLORS = [
  '#f6dcb8', '#eecaa0', '#e3b685', '#d4a06b', '#c08956',
  '#a87143', '#8b5933', '#6d4225', '#4f2f1b', '#372014',
];

const SHADES = [
  { name: 'Honey 4W',   color: '#d8a978' },
  { name: 'Caramel 5W', color: '#b88457' },
  { name: 'Bronze 6N',  color: '#8a5c3a' },
];

// Marker position (% along the scale) corresponding to "Warm Medium" — ~5th swatch
const SNAP_LEFT_PCT = 42;

interface AnimState {
  showAvatar: boolean;
  concernsCount: number;
  showScale: boolean;
  markerSnapped: boolean;
  showToneLabel: boolean;
  shadesCount: number;
  showBestMatch: boolean;
  showProduct: boolean;
  fadeOut: boolean;
}

const INITIAL: AnimState = {
  showAvatar: false,
  concernsCount: 0,
  showScale: false,
  markerSnapped: false,
  showToneLabel: false,
  shadesCount: 0,
  showBestMatch: false,
  showProduct: false,
  fadeOut: false,
};

export function SkinIntelligence(_props: SkinIntelligenceProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const [s, setS] = useState<AnimState>(INITIAL);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setS(INITIAL);
      await sleep(500);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, showAvatar: true }));
      await sleep(1100);

      // Stagger 3 concern dots + pills, 300ms apart (~1.2s total)
      for (let i = 1; i <= CONCERNS.length; i++) {
        if (!loopRef.current) return;
        setS((p) => ({ ...p, concernsCount: i }));
        await sleep(300);
      }
      await sleep(1500);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, showScale: true }));
      await sleep(350);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, markerSnapped: true }));
      await sleep(1050);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, showToneLabel: true }));
      await sleep(900);

      for (let i = 1; i <= SHADES.length; i++) {
        if (!loopRef.current) return;
        setS((p) => ({ ...p, shadesCount: i }));
        await sleep(300);
      }
      await sleep(500);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, showBestMatch: true }));
      await sleep(900);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, showProduct: true }));
      await sleep(1100);

      // Hold final state
      if (!loopRef.current) return;
      await sleep(2200);

      if (!loopRef.current) return;
      setS((p) => ({ ...p, fadeOut: true }));
      await sleep(650);

      if (!loopRef.current) return;
      await sleep(500);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  return (
    <div className={`skin-intelligence${s.fadeOut ? ' si-root-fade' : ''}`}>
      <div className="si-section-label sc-section-label">Skin analysis</div>

      <div className="si-avatar-wrap">
        <div className={`si-avatar${s.showAvatar ? ' is-visible' : ''}`}>
          {CONCERNS.map((c, i) => {
            const visible = s.concernsCount > i;
            return (
              <span
                key={`dot-${c.label}`}
                className={`si-dot${visible ? ' is-visible' : ''}`}
                style={{ left: `${c.dotX}%`, top: `${c.dotY}%` }}
              />
            );
          })}

          {CONCERNS.map((c, i) => {
            const visible = s.concernsCount > i;
            const style: React.CSSProperties = {
              left: `${c.dotX}%`,
              top: `${c.dotY}%`,
              transform: `translate(calc(-50% + ${c.pillX}px), calc(-50% + ${c.pillY}px))`,
            };
            return (
              <span
                key={`pill-${c.label}`}
                className={`si-concern-pill si-concern-pill-${c.pillSide}${visible ? ' is-visible' : ''}`}
                style={style}
              >
                {c.label}
              </span>
            );
          })}
        </div>
      </div>

      <div className="si-scale-section sc-glass-subtle">
        <div className="si-scale-header">
          <span className="si-scale-title">Skin tone</span>
          <span className={`si-tone-label${s.showToneLabel ? ' is-visible' : ''}`}>
            Warm Medium
          </span>
        </div>
        <div className={`si-scale${s.showScale ? ' is-visible' : ''}`}>
          {SCALE_COLORS.map((c, i) => (
            <span key={i} className="si-swatch" style={{ background: c }} />
          ))}
          <span
            className={`si-marker${s.showScale ? ' is-visible' : ''}${s.markerSnapped ? ' is-snapped' : ''}`}
            style={{ left: s.markerSnapped ? `${SNAP_LEFT_PCT}%` : '4%' }}
          />
        </div>
      </div>

      <div className="si-shades">
        {SHADES.map((sh, i) => {
          const visible = s.shadesCount > i;
          const isMiddle = i === 1;
          return (
            <div
              key={sh.name}
              className={`si-shade sc-glass-subtle${visible ? ' is-visible' : ''}${isMiddle && s.showBestMatch ? ' is-best' : ''}`}
            >
              <span className="si-shade-swatch" style={{ background: sh.color }} />
              <span className="si-shade-name">{sh.name}</span>
              {isMiddle && (
                <span className={`si-best-match${s.showBestMatch ? ' is-visible' : ''}`}>
                  <span className="sc-check">
                    <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="2.5 6 5 8.5 9.5 3.5" />
                    </svg>
                  </span>
                  Best match
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className={`si-product sc-glass-card${s.showProduct ? ' is-visible' : ''}`}>
        <div className="si-product-blob" />
        <div className="si-product-info">
          <span className="si-product-name">Featherlight Foundation</span>
          <span className="si-product-sub">Liquid · Satin finish</span>
        </div>
        <span className="sc-pill si-product-pill">Match: 96%</span>
      </div>
    </div>
  );
}
